import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AnnouncementsTab() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/notifications`, { withCredentials: true });
      setHistory(res.data.notifications || []);
    } catch (e) {}
  };

  const sendAnnouncement = async () => {
    if (!body.trim()) { toast.error('Message is required'); return; }
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/notifications/send`, {
        title: title.trim() || 'Announcement',
        body: body.trim()
      }, { withCredentials: true });
      toast.success('Notification sent to all players!');
      setTitle('');
      setBody('');
      fetchHistory();
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const clearAllNotifications = async () => {
    if (!window.confirm('This will permanently delete every in-app notification for ALL players. Continue?')) return;
    setClearing(true);
    try {
      const res = await axios.delete(`${BACKEND_URL}/api/admin/notifications/clear-all`, { withCredentials: true });
      toast.success(`Cleared ${res.data.deleted_count} notifications`);
      fetchHistory();
    } catch (error) {
      toast.error('Failed to clear notifications');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="announcements-tab">
      <div className="bg-card border border-zinc-800 p-6">
        <h2 className="font-heading font-bold text-2xl mb-1 flex items-center">
          <Send className="mr-2 text-purple-400" size={24} />
          Send Announcement
        </h2>
        <p className="text-zinc-400 text-sm mb-6">Send a push notification and in-app message to all players.</p>

        <div className="space-y-4 max-w-xl">
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-1">Title</label>
            <input
              data-testid="announcement-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Announcement"
              className="w-full bg-zinc-900 border border-zinc-700 px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-1">Message</label>
            <textarea
              data-testid="announcement-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your announcement..."
              rows={3}
              className="w-full bg-zinc-900 border border-zinc-700 px-4 py-2 text-white focus:border-purple-500 focus:outline-none resize-none"
            />
          </div>
          <button
            data-testid="send-announcement-btn"
            onClick={sendAnnouncement}
            disabled={sending || !body.trim()}
            className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-500 text-white px-6 py-2 font-bold transition-colors disabled:opacity-50"
          >
            <Send size={18} />
            <span>{sending ? 'Sending...' : 'Send to All Players'}</span>
          </button>
        </div>
      </div>

      <div className="bg-card border border-zinc-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg">Recent Notifications</h3>
          {history.length > 0 && (
            <button
              data-testid="clear-all-notifications-btn"
              onClick={clearAllNotifications}
              disabled={clearing}
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white px-3 py-1.5 text-xs font-bold transition-colors"
            >
              <Trash2 size={14} />
              <span>{clearing ? 'Clearing...' : 'Clear All'}</span>
            </button>
          )}
        </div>
        {history.length === 0 ? (
          <p className="text-zinc-500 text-sm">No notifications sent yet.</p>
        ) : (
          <div className="space-y-3">
            {history.map((n) => (
              <div key={n.notification_id} className="border border-zinc-700 p-3 flex items-start gap-3">
                <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                  n.type === 'admin' ? 'bg-purple-500' :
                  n.type === 'round_started' ? 'bg-green-500' :
                  n.type === 'results_published' ? 'bg-orange-500' :
                  n.type === 'selection_window' ? 'bg-blue-500' : 'bg-zinc-500'
                }`} />
                <div>
                  <p className="font-medium text-sm">{n.title}</p>
                  <p className="text-xs text-zinc-400">{n.body}</p>
                  <p className="text-xs text-zinc-600 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

